import React, { useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { AgentTool, useListScreensQuery, useListToolsQuery } from '@/redux/internal-services/agentsApi';
import {
  AgentFormValues, SectionCard,
  UNREACHABLE_MODULES, KNOWN_MODULES, screenMeta,
} from './agentShared';
import {
  canonicalModule, classifyPrompts, generalBlocksFor, legacyBlocksFor, moveWithinSubset, specificBlocksFor,
} from './promptPlacement';
import { computeToolPlacements, isUnplaced, sortToolNames } from './toolPlacement';
import PromptBlock from './PromptBlock';
import LoadTemplateButton from './LoadTemplateButton';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';


// ── tool picker ───────────────────────────────────────────────────────────────

export const ToolPicker: React.FC<{
  names: string[];
  selected: string[];
  onChange: (names: string[]) => void;
  label: string;
  emptyText?: string;
  /** Names already offered globally (every turn) — shown checked-but-disabled here rather than
   * hidden, so the admin can see WHY a tool isn't individually assignable to this screen instead
   * of wondering where it went. Omit for the Global tools picker itself, where nothing is locked. */
  globalNames?: Set<string>;
}> = ({ names, selected, onChange, label, emptyText, globalNames }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<string[]>(selected);
  const isGlobal = (name: string) => globalNames?.has(name) ?? false;

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    return names
      .filter((n) => !q || n.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => {
        const sa = selected.includes(a) || isGlobal(a), sb = selected.includes(b) || isGlobal(b);
        if (sa !== sb) return sa ? 1 : -1;
        return a.localeCompare(b);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names, query, open]);

  const openPicker = () => { setPending(selected); setOpen(true); };
  const closePicker = () => {
    setOpen(false);
    if (pending.length !== selected.length || pending.some((n) => !selected.includes(n))) {
      onChange(pending);
    }
  };
  const toggle = (name: string) => {
    if (isGlobal(name)) return;
    setPending((p) => (p.includes(name) ? p.filter((n) => n !== name) : [...p, name]));
  };

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        {selected.length > 0 && (
          <button type="button" onClick={openPicker} className="text-left font-mono text-xs text-ui-text2 hover:text-accent-text focus-ring">
            {selected.join(', ')}
          </button>
        )}
        {names.length > 0 ? (
          <Button type="button" variant="link" size="xs" className="shrink-0 text-accent-text" onClick={openPicker}>
            {selected.length > 0 ? 'edit' : label}
          </Button>
        ) : selected.length === 0 && emptyText ? (
          <p className="text-xs text-ui-text3">{emptyText}</p>
        ) : null}
      </div>

      {open && (
        <div className="mt-1 max-w-sm rounded-control border border-ui-border bg-ui-card p-2">
          <input
            autoFocus
            className={fieldClass({ size: 'sm' })}
            placeholder="Filter tools…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="mt-1 max-h-40 overflow-y-auto space-y-0.5">
            {options.length === 0 && <p className="text-xs text-ui-text3 px-1 py-1">No matches.</p>}
            {options.map((name) => {
              const global = isGlobal(name);
              return (
                <label
                  key={name}
                  title={global ? 'Enabled globally — not assigned to this context' : undefined}
                  className={cn(
                    'flex items-center gap-2 rounded-chip px-1 py-0.5 hover:bg-ui-hover',
                    global ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={global || pending.includes(name)}
                    disabled={global}
                    onChange={() => toggle(name)}
                    className="h-3 w-3 shrink-0 accent-accent"
                  />
                  <span className="text-xs font-mono truncate text-ui-text2 flex-1 min-w-0">{name}</span>
                  {global && <Badge variant="neutral" className="shrink-0 text-[10px] px-1.5 py-0">Global</Badge>}
                </label>
              );
            })}
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            {(() => {
              const selectable = options.filter((n) => !isGlobal(n));
              const allSelected = selectable.length > 0 && selectable.every((n) => pending.includes(n));
              return (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="text-ui-text3"
                  onClick={() =>
                    setPending((p) =>
                      allSelected
                        ? p.filter((n) => !selectable.includes(n))
                        : [...new Set([...p, ...selectable])]
                    )
                  }
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Button>
              );
            })()}
            <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={closePicker}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── legacy prompt row ────────────────────────────────────────────────────────
// A block that doesn't fit the new convention (condition_modules set, or topic_modules spanning
// more than one module) — still gates correctly at runtime (nothing here changes that), just
// surfaced for an explicit, admin-triggered "Convert" instead of silently migrated or hidden.

const LegacyPromptRow: React.FC<{
  index: number;
  module: string;
  onConvert: (kind: 'general' | 'specific') => void;
}> = ({ index, module, onConvert }) => {
  const { watch } = useFormContext<AgentFormValues>();
  const title = watch(`system_prompts.${index}.title`);
  const prompt = watch(`system_prompts.${index}.prompt`);
  const topicModules = watch(`system_prompts.${index}.topic_modules`) || [];
  const conditionModules = watch(`system_prompts.${index}.condition_modules`) || [];
  // Canonicalize before deduping — a block tagged with both "workflow" and the retired
  // "properties"/"quick_quote" aliases is one merged module, not two, so it should show once.
  const spans = [...new Set([...topicModules, ...conditionModules].map(canonicalModule))];

  return (
    <div className="overflow-hidden rounded-control border border-warning/30 bg-warning/10">
      <div className="flex flex-wrap items-center gap-2 px-2.5 py-1.5">
        <span className="text-xs font-medium text-ui-text truncate">{title || '(untitled)'}</span>
        <Badge variant="warning" className="shrink-0">Legacy prompt — uses the old module configuration</Badge>
        {spans.length > 1 && (
          <span className="text-xs text-ui-text3">scoped to: {spans.map((m) => screenMeta(m).label).join(', ')}</span>
        )}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={() => onConvert('general')}>
            Convert → General ({screenMeta(module).label})
          </Button>
          <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={() => onConvert('specific')}>
            Convert → Specific ({screenMeta(module).label})
          </Button>
        </div>
      </div>
      <p className="whitespace-pre-wrap bg-ui-card px-2.5 py-2 font-mono text-xs leading-relaxed text-ui-text2">
        {prompt || <span className="text-ui-text3">(empty)</span>}
      </p>
    </div>
  );
};

// ── one screen card ────────────────────────────────────────────────────────────

const ScreenCard: React.FC<{
  module: string;
  availableTools: string[];
  globalNames: Set<string>;
  selected: string[];
  onChange: (names: string[]) => void;
  generalIndices: number[];
  specificIndices: number[];
  legacyIndices: number[];
  onAddGeneral: () => void;
  onAddSpecific: () => void;
  onRemovePrompt: (index: number) => void;
  onMoveGeneral: (pos: number, dir: -1 | 1) => void;
  onMoveSpecific: (pos: number, dir: -1 | 1) => void;
  onConvertLegacy: (index: number, kind: 'general' | 'specific') => void;
}> = ({
  module, availableTools, globalNames, selected, onChange,
  generalIndices, specificIndices, legacyIndices,
  onAddGeneral, onAddSpecific, onRemovePrompt, onMoveGeneral, onMoveSpecific, onConvertLegacy,
}) => {
  const meta = screenMeta(module);
  const [open, setOpen] = useState(true);
  const count = selected.length + generalIndices.length + specificIndices.length;

  return (
    <section aria-label={meta.label} className="overflow-hidden rounded-card border border-ui-border3 bg-ui">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-ui-hover focus-ring"
      >
        <span className="w-3 shrink-0 text-xs text-ui-text3">{open ? '▾' : '▸'}</span>
        <h3 className="text-sm font-semibold text-ui-text">{meta.label}</h3>
        {meta.description && <span className="min-w-0 flex-1 truncate text-xs text-ui-text3">{meta.description}</span>}
        {count > 0 && <Badge variant="accent" title="Tools and prompt blocks on this screen">{count}</Badge>}
      </button>
      {open && (
      <div className="border-t border-ui-border p-4">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <LoadTemplateButton scope="module" templateKey={module} />
          </div>

          <div>
            <span className="text-xs font-medium text-ui-text2">
              Tools <span className="text-ui-text3 font-normal">({selected.length})</span>
            </span>
            <div className="mt-1">
              <ToolPicker
                names={availableTools}
                selected={selected}
                globalNames={globalNames}
                onChange={onChange}
                label="+ add tools to this screen"
                emptyText="No tools assigned to this screen yet."
              />
            </div>
            {globalNames.size > 0 && (
              <p className="text-xs text-ui-text3 mt-1">
                Tools enabled globally aren't listed again here — open the picker to see them marked "Global".
              </p>
            )}
          </div>

          {/* General — this module's own General block(s). Functionally unconditional (included
              every turn regardless of active screen, like a true-Global block), but shown here
              only — not aggregated from every other module's card, which would just repeat the
              same content everywhere. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-ui-text2">
                General prompt blocks <span className="text-ui-text3 font-normal">({generalIndices.length})</span>
                {' '}<Badge variant="accent" className="align-middle">Global</Badge>
              </span>
              <Button type="button" variant="soft" size="xs" onClick={onAddGeneral}>
                + Add prompt
              </Button>
            </div>
            {generalIndices.length === 0 ? (
              <EmptyState variant="inline" title="No General prompt block configured for this screen yet." />
            ) : (
              <div className="space-y-1.5">
                {generalIndices.map((idx, pos) => (
                  <PromptBlock
                    key={idx}
                    index={idx}
                    total={generalIndices.length}
                    onMove={(dir) => onMoveGeneral(pos, dir)}
                    onRemove={() => onRemovePrompt(idx)}
                    showScreenPicker={false}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Specific — only included while THIS screen is the active one. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-ui-text2">
                Specific prompt blocks <span className="text-ui-text3 font-normal">({specificIndices.length})</span>
                {' '}<Badge variant="neutral" className="align-middle">Screen Scoped</Badge>
              </span>
              <Button type="button" variant="soft" size="xs" onClick={onAddSpecific}>
                + Add prompt
              </Button>
            </div>
            {specificIndices.length === 0 ? (
              <EmptyState variant="inline" title="No prompt block scoped to this screen yet." />
            ) : (
              <div className="space-y-1.5">
                {specificIndices.map((idx, pos) => (
                  <PromptBlock
                    key={idx}
                    index={idx}
                    total={specificIndices.length}
                    onMove={(dir) => onMoveSpecific(pos, dir)}
                    onRemove={() => onRemovePrompt(idx)}
                    showScreenPicker={false}
                  />
                ))}
              </div>
            )}
          </div>

          {legacyIndices.length > 0 && (
            <div className="space-y-1.5">
              {legacyIndices.map((idx) => (
                <LegacyPromptRow
                  key={idx}
                  index={idx}
                  module={module}
                  onConvert={(kind) => onConvertLegacy(idx, kind)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </section>
  );
};

// ── ScreensTab ────────────────────────────────────────────────────────────────

const ScreensTab: React.FC = () => {
  const { watch, control, setValue } = useFormContext<AgentFormValues>();
  const { data: screens, isLoading } = useListScreensQuery();
  const { data: allTools } = useListToolsQuery();
  const { append, remove, move } = useFieldArray({ control, name: 'system_prompts' });

  // Tool placement source of truth: tool_scope_overrides on the agent form
  const watchedOverrides = watch('tool_scope_overrides');
  const overrides = useMemo(
    () => ({ global_tools: watchedOverrides?.global_tools ?? [], screens: watchedOverrides?.screens ?? {}, core_opt_out: watchedOverrides?.core_opt_out ?? [] }),
    [watchedOverrides],
  );

  const watchedTools = watch('tools');
  const selectedTools = useMemo(() => watchedTools || [], [watchedTools]);
  const attachedToolNames = useMemo(
    () => new Set(selectedTools.map((t) => t.name || t.tool_name).filter(Boolean) as string[]),
    [selectedTools],
  );
  const toolCatalogue = useMemo(() => {
    const m = new Map<string, AgentTool>();
    (allTools || []).forEach((t) => { if (t.name) m.set(t.name, t); });
    return m;
  }, [allTools]);

  // core_tools from API — pass the scope filter unconditionally when attached, but editable like any other tool
  const coreTools = useMemo(() => screens?.core_tools ?? [], [screens]);
  // available_tools from API — picker pool (all registered, minus retired/realtime-only)
  const availableTools = useMemo(() => screens?.available_tools ?? [], [screens]);

  const applyAttachDelta = (toAdd: string[], toRemove: string[]) => {
    if (!toAdd.length && !toRemove.length) return;
    const removeSet = new Set(toRemove);
    const kept = selectedTools.filter((t) => !removeSet.has((t.name || t.tool_name) as string));
    const added = toAdd
      .map((name) => toolCatalogue.get(name))
      .filter((t): t is AgentTool => Boolean(t))
      .map((t) => ({
        tool_id: t.tool_id, name: t.name, tool_name: t.tool_name || t.name, description: t.description,
        requires_confirmation: t.requires_confirmation ?? false, argument_schema: t.argument_schema,
        toolkit_type: t.toolkit_type, toolkit_name: t.toolkit_name, action_card_template: t.action_card_template,
      }));
    setValue('tools', [...kept, ...added], { shouldDirty: true });
  };

  // Placement changes (this screen's picker, or Global) only ever ATTACH a newly-checked tool —
  // unchecking one only ever edits the override, never detaches it from the agent. Otherwise a
  // tool could silently disappear from the Tools tab (losing any per-agent customization) just
  // because an admin unassigned it from the one screen it happened to be on; "owned but placed
  // nowhere" is instead a real, visible state (the "Not placed" badge / callout below). Ownership
  // removal is only ever explicit, via the Tools tab's own Remove button.
  const applyAssignment = (
    currentOverride: string[],
    pending: string[],
    writeOverride: (names: string[]) => void,
  ) => {
    const toAttach = pending.filter((n) => !attachedToolNames.has(n));
    if (toAttach.length) applyAttachDelta(toAttach, []);

    const overrideAdds = pending.filter((n) => !currentOverride.includes(n));
    const overrideRemoves = currentOverride.filter((n) => !pending.includes(n));
    if (overrideAdds.length || overrideRemoves.length) {
      const removeSet = new Set(overrideRemoves);
      writeOverride([...currentOverride.filter((n) => !removeSet.has(n)), ...overrideAdds]);
    }
  };

  // Global tools picker. Unchecking a tool NEVER detaches it from the agent: it stays enabled and shows up under "Not placed"
  // until it is placed again or removed for good from the Tools tab. A non-core tool just loses its `global_tools` entry; a core
  // tool (a fixed backend list, always global by default) is recorded in `core_opt_out` instead, which the backend honours.
  const applyGlobalAssignment = (pending: string[]) => {
    const toAttach = pending.filter((n) => !attachedToolNames.has(n));
    applyAttachDelta(toAttach, []);

    const coreSet = new Set(coreTools);
    const nextOptOut = new Set(overrides.core_opt_out ?? []);
    coreTools.forEach((n) => {
      if (pending.includes(n)) nextOptOut.delete(n);
      else if (globalSelected.includes(n)) nextOptOut.add(n);
    });
    const nonCorePending = pending.filter((n) => !coreSet.has(n));
    const keptGlobal = overrides.global_tools.filter((n) => nonCorePending.includes(n));
    const nextGlobal = [...keptGlobal, ...nonCorePending.filter((n) => !overrides.global_tools.includes(n))];
    setValue(
      'tool_scope_overrides',
      { ...overrides, global_tools: nextGlobal, core_opt_out: [...nextOptOut].sort() },
      { shouldDirty: true },
    );
  };
  const setScreenOverrides = (module: string, names: string[]) => {
    const next = { ...overrides.screens };
    if (names.length > 0) next[module] = names; else delete next[module];
    // Workflow + Properties + Quick Quote module merge: the first edit through the merged card's
    // picker consolidates any legacy separate screens.properties/.quick_quote into screens.workflow
    // (the backend's page_tools_map fallback already reads them as a union either way, so this is
    // a one-time tidy-up on write, never a silent change just from opening the page).
    if (module === 'workflow') {
      delete next.properties;
      delete next.quick_quote;
    }
    setValue('tool_scope_overrides', { ...overrides, screens: next }, { shouldDirty: true });
  };

  // useWatch, not watch('system_prompts') — a plain imperative watch() alongside
  // useFieldArray on the SAME field name does not reliably re-render on a nested VALUE change
  // made via setValue (e.g. convertLegacy below setting .topic_modules/.home_module on an
  // existing item) — only on structural changes (append/remove/move). useWatch is RHF's
  // documented fix for exactly this: subscribing a render to live nested value changes.
  const watchedSystemPrompts = useWatch({ control, name: 'system_prompts' });
  const systemPrompts = useMemo(() => watchedSystemPrompts || [], [watchedSystemPrompts]);
  // Single source of truth for "what kind of prompt block is this" — shared with the Instructions
  // tab so the two views can never classify the same agent's blocks differently (promptPlacement.ts).
  const placements = useMemo(() => classifyPrompts(systemPrompts), [systemPrompts]);

  const addGeneralFor = (module: string) => append({ title: '', prompt: '', home_module: module });
  const addSpecificFor = (module: string) => append({ title: '', prompt: '', topic_modules: [module] });
  const moveWithin = (subset: number[], pos: number, dir: -1 | 1) => {
    const pair = moveWithinSubset(subset, pos, dir);
    if (pair) move(pair[0], pair[1]);
  };
  const convertLegacy = (index: number, kind: 'general' | 'specific', module: string) => {
    setValue(`system_prompts.${index}.condition_modules`, undefined, { shouldDirty: true });
    setValue(`system_prompts.${index}.topic_modules`, kind === 'specific' ? [module] : undefined, { shouldDirty: true });
    setValue(`system_prompts.${index}.home_module`, kind === 'general' ? module : undefined, { shouldDirty: true });
  };

  // Single source of truth for "where is this tool placed" — shared with the Tools tab so the two
  // views can never classify the same agent differently (see toolPlacement.ts).
  const blockToolSources = useWatch({ control, name: 'system_prompts' });
  const toolPlacements = useMemo(
    () => computeToolPlacements(attachedToolNames, overrides, coreTools, blockToolSources ?? []),
    [attachedToolNames, overrides, coreTools, blockToolSources],
  );
  const globalToolSet = useMemo(
    () => new Set([...toolPlacements].filter(([, p]) => p.isGlobal).map(([n]) => n)),
    [toolPlacements],
  );
  // Global tools: core + whatever is in overrides.global_tools, limited to what's actually attached
  const globalSelected = useMemo(() => sortToolNames([...globalToolSet]), [globalToolSet]);

  // Owned but placed nowhere (not global, not on any screen) — SAM never offers these in scoped
  // mode even though they look "enabled" in the Tools tab. Surfaced here too, where placement
  // changes actually happen, not just in the other tab.
  // Exclude tools not in availableTools (realtime-only / retired tools are intentionally unplaceable
  // via the UI and never offered by static placement, so they're not a bug).
  const availableToolSet = useMemo(() => new Set(availableTools), [availableTools]);
  // The pickers offer only the tools enabled on this agent (the Tools tab's "Enabled tools" list), not the whole catalogue.
  const pickerTools = useMemo(() => availableTools.filter((n) => attachedToolNames.has(n)), [availableTools, attachedToolNames]);
  const unplacedTools = useMemo(
    () =>
      sortToolNames(
        [...toolPlacements].filter(([n, p]) => isUnplaced(p) && availableToolSet.has(n)).map(([n]) => n),
      ),
    [toolPlacements, availableToolSet],
  );

  // Module list: known modules minus unreachable ones, plus any extra from overrides.screens
  // Which module's screen is shown: the module list above picks one; by default (null) all modules are shown, folded sections and all.
  const [pickedModule, setPickedModule] = useState<string | 'all' | null>(null);

  // Card / sub-card placement keys ("engagement.view", "engagement.view#proposal") share `screens` with module
  // slugs but are edited under their module's "Cards & sections", never as screens of their own.
  const modules = useMemo(() => {
    const extra = Object.keys(overrides.screens)
      .filter((m) => !KNOWN_MODULES.includes(m) && !m.includes('.') && !m.includes('#'));
    return [...KNOWN_MODULES, ...extra].filter((m) => !UNREACHABLE_MODULES.has(m));
  }, [overrides.screens]);


  const activeModule: string | 'all' = pickedModule && (pickedModule === 'all' || modules.includes(pickedModule))
    ? pickedModule
    : 'all';

  return (
    <>
      <SectionCard
        title="Modules"
        desc="Configure which tools are available globally (every turn) and per-screen (only when that screen is open). Tool placement is saved with the agent — no code deploy needed."
      >
        {isLoading && <LoadingState label="Loading tool catalogue…" className="py-6" />}

        {screens && (
          <>
            {/* Global tools — core + admin-configured tools, all offered every turn, all editable */}
            <div className="rounded-control border border-ui-border bg-ui-subtle px-3 py-2.5">
              <p className="text-xs font-medium text-ui-text2 mb-1">
                Global tools <span className="text-ui-text3 font-normal">— offered every turn ({globalSelected.length})</span>
              </p>
              <ToolPicker
                names={pickerTools}
                selected={globalSelected}
                onChange={applyGlobalAssignment}
                label="+ add global tools"
              />
            </div>

            {unplacedTools.length > 0 && (
              <div className="rounded-control border border-warning/30 bg-warning/10 px-3 py-2.5 mt-3">
                <p className="text-xs font-medium text-warning-text mb-1">
                  Not placed anywhere <span className="font-normal">({unplacedTools.length})</span>
                </p>
                <p className="text-xs font-mono text-ui-text2">{unplacedTools.join(', ')}</p>
                <p className="text-xs text-ui-text3 mt-1">
                  Attached to this agent but not offered globally or on any screen — SAM won't use them. Add them to
                  Global tools above or to a screen below, or remove them for good from the Tools tab.
                </p>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {screens && (
        <div className="space-y-3">
          <nav aria-label="Modules" className="card-surface flex flex-wrap items-center gap-1.5 p-3">
            {modules.map((m) => (
              <Chip key={m} selected={activeModule === m} onClick={() => setPickedModule(m)}>
                {screenMeta(m).label}
              </Chip>
            ))}
            <Chip selected={activeModule === 'all'} onClick={() => setPickedModule('all')} className="ml-auto">
              All modules
            </Chip>
          </nav>
          {modules.filter((m) => activeModule === 'all' || m === activeModule).map((module) => {
            // Workflow + Properties + Quick Quote merge: read the union of the (possibly still
            // separate, pre-migration) old keys — the backend's page_tools_map fallback already
            // treats them this way at runtime either way, so this is correct even before any edit.
            const screenOverride = module === 'workflow'
              ? [...new Set([
                  ...(overrides.screens.workflow ?? []),
                  ...(overrides.screens.properties ?? []),
                  ...(overrides.screens.quick_quote ?? []),
                ])]
              : (overrides.screens[module] ?? []);
            const selected = sortToolNames(
              [...new Set(screenOverride)].filter((n) => attachedToolNames.has(n) && !globalToolSet.has(n)),
            );
            const generalIndices = generalBlocksFor(placements, module);
            const specificIndices = specificBlocksFor(placements, module);
            const legacyIndices = legacyBlocksFor(placements, module);
            return (
              <ScreenCard
                key={module}
                module={module}
                availableTools={pickerTools}
                globalNames={globalToolSet}
                selected={selected}
                onChange={(pending) => applyAssignment(screenOverride, pending, (names) => setScreenOverrides(module, names))}
                generalIndices={generalIndices}
                specificIndices={specificIndices}
                legacyIndices={legacyIndices}
                onAddGeneral={() => addGeneralFor(module)}
                onAddSpecific={() => addSpecificFor(module)}
                onRemovePrompt={remove}
                onMoveGeneral={(pos, dir) => moveWithin(generalIndices, pos, dir)}
                onMoveSpecific={(pos, dir) => moveWithin(specificIndices, pos, dir)}
                onConvertLegacy={(idx, kind) => convertLegacy(idx, kind, module)}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

export default ScreensTab;
